import React from 'react'
import { Skeleton } from '@mui/material'

const BONE = '#E9ECF1'

/**
 * Neutral placeholder shown while the checkout details (and the merchant's
 * branding) are loading, so no default AautiPay branding flashes first.
 *
 * @param {Object} props
 * @param {'summary'|'paymentMethod'|'status'} [props.variant='summary'] - Layout to mirror:
 *   'summary' = payment link price card, 'paymentMethod' = iframe / props checkout,
 *   'status' = payment status page
 * @param {boolean} [props.showHeader=true] - Show the header bar placeholder
 * @param {boolean} [props.showFooter=true] - Show the footer placeholder
 */
const CheckoutSkeleton = ({ variant = 'summary', showHeader = true, showFooter = true }) => {
  const bone = { bgcolor: BONE }
  const isPaymentMethod = variant === 'paymentMethod'
  const isStatus = variant === 'status'

  return (
    <div
      aria-busy='true'
      aria-label='Loading checkout'
      style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#FFFFFF' }}
    >
      {showHeader && (
        <div style={{ padding: '15px 20px', flexShrink: 0, backgroundColor: '#F4F6F9' }}>
          <Skeleton variant='rounded' animation='wave' width={120} height={22} sx={bone} />
        </div>
      )}

      {isStatus ? <StatusBones bone={bone} /> : isPaymentMethod ? <PaymentMethodBones bone={bone} /> : (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            border: '1px solid #EEF1F5',
            borderRadius: 14,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Merchant */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Skeleton variant='rounded' animation='wave' width={38} height={38} sx={{ ...bone, borderRadius: '10px' }} />
            <div style={{ flex: 1 }}>
              <Skeleton animation='wave' width='45%' height={20} sx={bone} />
              <Skeleton animation='wave' width='30%' height={16} sx={bone} />
            </div>
          </div>

          {/* Price lines */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 0', borderTop: '1px dashed #EEF1F5', borderBottom: '1px dashed #EEF1F5' }}>
            {[0, 1, 2].map((row) => (
              <div key={row} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Skeleton animation='wave' width='35%' height={18} sx={bone} />
                <Skeleton animation='wave' width='18%' height={18} sx={bone} />
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton animation='wave' width='32%' height={20} sx={bone} />
            <Skeleton animation='wave' width='25%' height={30} sx={bone} />
          </div>

          {/* Pay button */}
          <Skeleton variant='rounded' animation='wave' width='100%' height={50} sx={{ ...bone, borderRadius: '12px' }} />
        </div>
      </div>
      )}

      {showFooter && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 16, flexShrink: 0 }}>
          <Skeleton animation='wave' width={130} height={16} sx={bone} />
        </div>
      )}
    </div>
  )
}

// Mirrors the "Payment method" column of CardDetails
const PaymentMethodBones = ({ bone }) => {
  const radioRow = (highlighted) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', backgroundColor: highlighted ? '#F7F9FC' : 'transparent' }}>
      <Skeleton variant='circular' animation='wave' width={22} height={22} sx={bone} />
      <Skeleton variant='rounded' animation='wave' width={38} height={26} sx={{ ...bone, borderRadius: '5px' }} />
      <Skeleton animation='wave' width={110} height={20} sx={bone} />
    </div>
  )

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', justifyContent: 'center', padding: '32px 20px' }}>
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* "Payment method" title */}
        <Skeleton animation='wave' width={150} height={26} sx={bone} />

        {/* Card options with the card form */}
        <div style={{ border: '1px solid #EEF1F5', borderRadius: 12, overflow: 'hidden' }}>
          {radioRow(true)}
          <div style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Skeleton animation='wave' width={120} height={20} sx={bone} />
            <Skeleton variant='rounded' animation='wave' width='100%' height={52} sx={{ ...bone, borderRadius: '8px' }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <Skeleton variant='rounded' animation='wave' width='50%' height={52} sx={{ ...bone, borderRadius: '8px' }} />
              <Skeleton variant='rounded' animation='wave' width='50%' height={52} sx={{ ...bone, borderRadius: '8px' }} />
            </div>
            <Skeleton animation='wave' width='75%' height={16} sx={bone} />
          </div>
          <div style={{ borderTop: '1px solid #EEF1F5' }}>{radioRow(false)}</div>
        </div>

        {/* "Save my information" box */}
        <div style={{ border: '1px solid #EEF1F5', borderRadius: 12, padding: '16px 20px', display: 'flex', gap: 14 }}>
          <Skeleton variant='rounded' animation='wave' width={20} height={20} sx={{ ...bone, borderRadius: '4px', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Skeleton animation='wave' width='60%' height={20} sx={bone} />
            <Skeleton animation='wave' width='80%' height={16} sx={bone} />
          </div>
        </div>

        {/* Pay / Subscribe button */}
        <Skeleton variant='rounded' animation='wave' width='100%' height={52} sx={{ ...bone, borderRadius: '12px' }} />

        {/* Consent text */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Skeleton animation='wave' width='85%' height={16} sx={bone} />
          <Skeleton animation='wave' width='45%' height={16} sx={bone} />
        </div>
      </div>
    </div>
  )
}

// Mirrors PaymentStatus: status icon, title and message
const StatusBones = ({ bone }) => (
  <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', gap: 10 }}>
    <Skeleton variant='circular' animation='wave' width={70} height={70} sx={{ ...bone, mb: 1.5 }} />
    <Skeleton animation='wave' width={180} height={26} sx={bone} />
    <Skeleton animation='wave' width={240} height={18} sx={bone} />
  </div>
)

export default CheckoutSkeleton
